'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import TodoList from '@/components/todos/TodoList';
import TodoArchive from '@/components/todos/TodoArchive';
import { useAuth } from '@/lib/auth-context';

interface Todo {
  id: string;
  title: string;
  details: string | null;
  notes: string | null;
  due_date: string | null;
  delegate: string | null;
  sort_order: number;
  deleted_at: string | null;
  archived_at: string | null;
  decision_id: string | null;
  suggestion_reason: string | null;
  confidence: number | null;
  sensitive: boolean;
  critical: boolean;
  personal: boolean;
}

type Scope = 'all' | 'work' | 'personal';

function TodosContent() {
  const { permissions, isLoading: authLoading } = useAuth();
  const searchParams = useSearchParams();
  const editId = searchParams.get('edit');
  const [todos, setTodos] = useState<Todo[]>([]);
  const [archivedTodos, setArchivedTodos] = useState<Todo[]>([]);
  const [showArchive, setShowArchive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [delegateFilter, setDelegateFilter] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [scope, setScope] = useState<Scope>('all');

  // Get unique individual delegates from todos (split by comma/space, case-insensitive)
  const delegateMap = new Map<string, string>();
  todos
    .filter(t => t.delegate && !t.deleted_at)
    .flatMap(t => t.delegate!.split(/[,\s]+/).filter(Boolean).map(d => d.replace(/^@/, '')))
    .forEach(d => {
      const key = d.toLowerCase();
      // Keep the first-seen capitalization, or prefer capitalized version
      if (!delegateMap.has(key) || d[0] === d[0].toUpperCase()) {
        delegateMap.set(key, d.charAt(0).toUpperCase() + d.slice(1).toLowerCase());
      }
    });
  const delegates = Array.from(delegateMap.values()).sort();

  // Filter todos based on search query and selected delegate
  const filteredTodos = todos.filter(t => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesTitle = t.title.toLowerCase().includes(query);
      const matchesDetails = t.details?.toLowerCase().includes(query);
      const matchesNotes = t.notes?.toLowerCase().includes(query);
      if (!matchesTitle && !matchesDetails && !matchesNotes) return false;
    }

    // Delegate filter (case-insensitive)
    if (delegateFilter) {
      if (!t.delegate) return false;
      const todosDelegates = t.delegate.split(/[,\s]+/).filter(Boolean).map(d => d.replace(/^@/, '').toLowerCase());
      if (!todosDelegates.includes(delegateFilter.toLowerCase())) return false;
    }

    return true;
  });

  const fetchTodos = async (currentScope?: Scope) => {
    const s = currentScope ?? scope;
    try {
      const [todosRes, archivedRes] = await Promise.all([
        fetch(`/api/todos?scope=${s}`),
        fetch(`/api/todos?archived=true&scope=${s}`),
      ]);

      if (todosRes.ok) {
        const data = await todosRes.json();
        setTodos(data.todos);
      }

      if (archivedRes.ok) {
        const data = await archivedRes.json();
        setArchivedTodos(data.todos);
      }
    } catch (error) {
      console.error('Failed to fetch todos:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTodos();
  }, []);

  const handleScopeChange = (newScope: Scope) => {
    setScope(newScope);
    setDelegateFilter(null);
    setLoading(true);
    fetchTodos(newScope);
  };

  // Refresh todos when archive changes
  const handleArchiveChange = () => {
    fetchTodos();
  };

  const handleAutoArchive = async () => {
    try {
      const response = await fetch('/api/todos/auto-archive', { method: 'POST' });
      if (!response.ok) throw new Error('Failed to auto-archive');
      const data = await response.json();
      if (data.archived > 0) {
        fetchTodos();
      }
    } catch (error) {
      console.error('Failed to auto-archive:', error);
    }
  };

  if (authLoading) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">Todos</h1>
          <p className="mt-1 text-zinc-600 dark:text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }

  if (!permissions.canWrite) {
    return (
      <div className="flex flex-col h-[calc(100vh-113px)] max-w-6xl mx-auto px-4 pt-4 pb-2">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center space-y-4">
            <div className="w-16 h-16 mx-auto bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Access Denied
            </h2>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">
              Only administrators and delegates can manage todos.
            </p>
            <Link
              href="/"
              className="inline-block px-4 py-2 bg-zinc-200 dark:bg-zinc-800 hover:bg-zinc-300 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg transition-colors text-sm"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">Todos</h1>
          <p className="mt-1 text-zinc-600 dark:text-zinc-400">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">Todos</h1>
          <p className="mt-1 text-zinc-600 dark:text-zinc-400">
            Manage your tasks and track what needs to be done
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search todos..."
            className="px-4 py-2 border border-zinc-300 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-800 text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-zinc-400 dark:placeholder-zinc-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="px-3 py-2 text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
              aria-label="Clear search"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Active Todos */}
      <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800 p-6">
        <div className="flex justify-between items-center mb-4">
          <div style={{ display: 'flex', alignItems: 'center', gap: 36 }}>
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Active Todos
              <span className="ml-4 text-zinc-500 dark:text-zinc-400 font-normal">
                ({filteredTodos.filter(t => !t.deleted_at).length})
              </span>
            </h2>
            {/* Scope filter pills — admin only, pinned left next to title */}
            {permissions.canViewPersonal && (
              <div className="flex gap-1">
                {(['all', 'work', 'personal'] as Scope[]).map((s) => (
                  <button
                    key={s}
                    onClick={() => handleScopeChange(s)}
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      scope === s
                        ? s === 'personal'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900'
                        : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                    }`}
                  >
                    {s.charAt(0).toUpperCase() + s.slice(1)}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Delegate filter pills — right side, hidden when scope is personal */}
          {scope !== 'personal' && delegates.length > 0 && (
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setDelegateFilter(null)}
                className={`px-3 py-1 rounded-full text-sm transition-colors ${
                  delegateFilter === null
                    ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900'
                    : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                }`}
              >
                All
              </button>
              {delegates.map((delegate) => (
                <button
                  key={delegate}
                  onClick={() => setDelegateFilter(delegate)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    delegateFilter === delegate
                      ? 'bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900'
                      : 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                  }`}
                >
                  @{delegate}
                </button>
              ))}
            </div>
          )}
        </div>
        <TodoList
          initialTodos={filteredTodos}
          compact={false}
          showAddForm={true}
          initialEditId={editId}
          onTodoChange={fetchTodos}
          scope={scope}
        />
      </div>

      {/* Archived Section */}
      <div className="bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-800">
        <div className="flex items-center p-6 gap-4">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
              Archived Todos
            </h2>
            <p className="text-sm text-zinc-500 dark:text-zinc-400">
              {archivedTodos.length} archived item{archivedTodos.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={handleAutoArchive}
            className="text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 whitespace-nowrap"
          >
            Archive completed (6+ days)
          </button>
          <button
            onClick={() => setShowArchive(!showArchive)}
            className="p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded transition-colors"
          >
            <svg
              className={`w-5 h-5 text-zinc-400 transform transition-transform ${
                showArchive ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>

        {showArchive && (
          <div className="px-6 pb-6">
            <TodoArchive initialTodos={archivedTodos} onTodoChange={fetchTodos} />
          </div>
        )}
      </div>
    </div>
  );
}

export default function TodosPage() {
  return (
    <Suspense fallback={
      <div className="space-y-8">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-zinc-100">Todos</h1>
          <p className="mt-1 text-zinc-600 dark:text-zinc-400">Loading...</p>
        </div>
      </div>
    }>
      <TodosContent />
    </Suspense>
  );
}
